/**
 * Color Filter Utilities
 * Converts hex colors and effect settings to CSS filters for real-time preview
 * These filters approximate FFmpeg color effects for immediate visual feedback
 */

import { MediaItem } from '../types';

/**
 * Build complete CSS filter string from MediaItem effects
 * Combines color tint, saturation, sepia, vignette, contrast, and brightness
 * 
 * @param mediaItem - Media item with effect properties
 * @returns CSS filter string
 */
export function buildMediaItemFilterCSS(mediaItem: MediaItem): string {
  const filters: string[] = [];
  
  // Saturation (0 = B&W, 1 = normal, 2 = super saturated)
  if (mediaItem.saturation !== undefined) {
    filters.push(`saturate(${mediaItem.saturation})`);
  }
  
  // Sepia effect (0 = off, 0.5 = subtle, 1 = full)
  if (mediaItem.sepia !== undefined && mediaItem.sepia > 0) {
    filters.push(`sepia(${mediaItem.sepia})`);
  }
  
  // Contrast (0.5 = low, 1 = normal, 2 = high)
  if (mediaItem.contrast !== undefined && mediaItem.contrast !== 1) {
    filters.push(`contrast(${mediaItem.contrast})`);
  }
  
  // Brightness (0.5 = dark, 1 = normal, 1.5 = bright)
  if (mediaItem.brightness !== undefined && mediaItem.brightness !== 1) {
    filters.push(`brightness(${mediaItem.brightness})`);
  }
  
  // Color tint (if provided)
  if (mediaItem.color) {
    const hue = hexToHue(mediaItem.color);
    const saturation = hexToSaturation(mediaItem.color);
    filters.push(`sepia(0.5) saturate(${saturation}) hue-rotate(${hue}deg)`);
  }
  
  return filters.length > 0 ? filters.join(' ') : '';
}

/**
 * Convert hex color to CSS filter string for real-time preview
 * Applies a sepia base, saturation boost, and hue rotation to simulate color tinting
 * 
 * @param hexColor - Hex color string (e.g., "#FF0000")
 * @returns CSS filter string
 */
export function buildColorFilterCSS(hexColor: string): string {
    const hue = hexToHue(hexColor);
    const saturation = hexToSaturation(hexColor);
    
    // Apply sepia base (50%) + hue rotation + saturation boost
    // This creates a color tint effect similar to FFmpeg's hue filter
    return `sepia(0.5) saturate(${saturation}) hue-rotate(${hue}deg) brightness(1.1)`;
  }
  
  /**
   * Convert hex color to hue degree (0-360)
   * Uses HSL color model calculation
   * 
   * @param hex - Hex color string (e.g., "#FF0000")
   * @returns Hue degree (0-360)
   */
  export function hexToHue(hex: string): number {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert to RGB (0-1 range)
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    
    let hue = 0;
    
    if (delta === 0) {
      // Grayscale - no hue
      hue = 0;
    } else if (max === r) {
      // Red is dominant
      hue = 60 * (((g - b) / delta) % 6);
    } else if (max === g) {
      // Green is dominant
      hue = 60 * (((b - r) / delta) + 2);
    } else {
      // Blue is dominant
      hue = 60 * (((r - g) / delta) + 4);
    }
    
    // Normalize to 0-360 range
    return hue < 0 ? hue + 360 : hue;
  }
  
  /**
   * Calculate saturation multiplier from hex color
   * Purer colors get higher saturation boost
   * 
   * @param hex - Hex color string (e.g., "#FF0000")
   * @returns Saturation value (1.5-3.5 range for CSS)
   */
  export function hexToSaturation(hex: string): number {
    // Remove # if present
    hex = hex.replace('#', '');
    
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    
    // Calculate color variance (how far from grayscale)
    const avg = (r + g + b) / 3;
    const variance = Math.sqrt(
      ((r - avg) ** 2 + (g - avg) ** 2 + (b - avg) ** 2) / 3
    );
    
    // Map variance (0-128) to saturation (1.5-3.5)
    // Higher variance = more colorful = higher saturation boost
    return 1.5 + (variance / 128) * 2;
  }
  
  /**
   * Validate hex color format
   * 
   * @param hex - String to validate
   * @returns True if valid hex color
   */
  export function isValidHexColor(hex: string): boolean {
    const hexPattern = /^#?[0-9A-Fa-f]{6}$/;
    return hexPattern.test(hex);
  }
  
  /**
   * Get preset color options for quick selection
   */
  export const COLOR_PRESETS = {
    none: null,
    warm: '#FFA500',      // Orange - warm tone
    cool: '#4A90E2',      // Blue - cool tone
    vintage: '#D4A574',   // Sepia brown - vintage look
    vibrant: '#FF00FF',   // Magenta - vibrant pop
    cinematic: '#1E3A5F', // Deep blue - cinematic
    sunset: '#FF6B6B',    // Coral red - sunset
    forest: '#2D5016',    // Dark green - nature
    neon: '#00FF00',      // Bright green - neon
  } as const;
  
  export type ColorPreset = keyof typeof COLOR_PRESETS;